import { useId } from "react";
import type { SelectOption } from "./SelectDropdown";
import { SelectDropdown } from "./SelectDropdown";
import {
  FieldRow,
  FieldRowControl,
  FieldRowStack,
  FormSection,
  HelpText,
  Input,
  Label,
  SectionTitle,
} from "./styles";

export interface PostgresCdcFieldsProps {
  targetService: string;
  setTargetService: (value: string) => void;
  targetOptions: SelectOption<string>[];
  targetsLoading: boolean;
  table: string;
  setTable: (value: string) => void;
  tableOptions: SelectOption<string>[];
  tablesLoading: boolean;
  /** The slot the service will read, derived from its name and table. */
  slot: string;
  heartbeat: string;
  setHeartbeat: (value: string) => void;
}

/**
 * Configuration for PostgreSQL change capture per Figma 16309-26641. It
 * reads through a PostgreSQL service, so it carries no credentials of its
 * own; what it adds is the replication slot, which one pipeline owns at a
 * time. Every field is an inline label + control row; the Table row nests
 * under the service it depends on.
 */
export function PostgresCdcFields(props: PostgresCdcFieldsProps) {
  return (
    <FormSection>
      <SectionTitle>Change capture configuration</SectionTitle>
      <TargetField {...props} />
      <HeartbeatField {...props} />
    </FormSection>
  );
}

function TargetField(props: PostgresCdcFieldsProps) {
  const { targetService, setTargetService, targetOptions, targetsLoading } =
    props;
  const noTargets = !targetsLoading && targetOptions.length === 0;
  return (
    <FieldRow>
      <Label as="span">PostgreSQL service*</Label>
      <FieldRowStack>
        <FieldRowControl>
          <SelectDropdown
            value={targetService}
            options={targetOptions}
            onChange={setTargetService}
            onClear={() => setTargetService("")}
            disabled={targetsLoading || noTargets}
            placeholder="Select a service"
          />
          <HelpText>
            {noTargets
              ? "No PostgreSQL services yet. Add one first; change capture reads through it."
              : "The database to capture changes from. Its connection and credentials stay with it. The database needs wal_level = logical."}
          </HelpText>
        </FieldRowControl>
        <TableField {...props} />
      </FieldRowStack>
    </FieldRow>
  );
}

function TableField({
  table,
  setTable,
  tableOptions,
  tablesLoading,
  targetService,
  slot,
}: PostgresCdcFieldsProps) {
  const noTables =
    !!targetService && !tablesLoading && tableOptions.length === 0;
  return (
    <FieldRow>
      <Label as="span">Table*</Label>
      <FieldRowControl>
        <SelectDropdown
          value={table}
          options={tableOptions}
          onChange={setTable}
          disabled={!targetService || tablesLoading || noTables}
          placeholder={
            targetService ? "Select a table" : "Choose a service first"
          }
        />
        <HelpText>
          {noTables
            ? "This service has no tables to capture."
            : "One table per change capture service. Its replication slot and publication are named after the service and the table."}
        </HelpText>
        {slot && (
          <HelpText>
            Slot <code>{slot}</code>, publication <code>{slot}_pub</code>. The
            slot keeps its WAL on the server until it is released.
          </HelpText>
        )}
      </FieldRowControl>
    </FieldRow>
  );
}

function HeartbeatField({ heartbeat, setHeartbeat }: PostgresCdcFieldsProps) {
  const heartbeatId = useId();
  return (
    <FieldRow>
      <Label htmlFor={heartbeatId}>Heartbeat (seconds)</Label>
      <FieldRowControl>
        <Input
          id={heartbeatId}
          type="text"
          inputMode="numeric"
          value={heartbeat}
          onChange={(e) => setHeartbeat(e.target.value)}
          placeholder="10"
        />
        <HelpText>
          How often an idle capture confirms its position, so the slot does not
          hold WAL while the tables are quiet. Defaults to 10.
        </HelpText>
      </FieldRowControl>
    </FieldRow>
  );
}
